import { isValidElement, memo, useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlertTriangle, Check, Copy, Info, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';

import { ApprovalInteraction } from '@/components/ApprovalInteraction';
import { CarrierAuditReview } from '@/components/CarrierAuditReview';
import { CarrierInitializationOverview } from '@/components/CarrierInitializationOverview';
import { CarrierMappingDrafts } from '@/components/CarrierMappingDrafts';
import { DomesticServiceRateSummary } from '@/components/DomesticServiceRateSummary';
import { DomesticTrafficPerformance } from '@/components/DomesticTrafficPerformance';
import { ServiceDefinitionForm } from '@/components/ServiceDefinitionForm';
import { parseCarrierInitializationSurface } from '@/components/carrierInitializationSurface';
import { BrandLogo } from '@/components/ui/BrandLogo';
import { formatServerTimestamp } from '@/lib/timestamps';
import { cn } from '@/lib/utils';
import {
  api,
  type ChatInteraction,
  type ChatInteractionSubmission,
} from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { useSettingsStore } from '@/stores/settingsStore';
import type { LocalMessage } from '@/stores/chatStore';
import type { ChatContentBlock } from '@/hooks/useWebSocket';
import { AgentActivity } from './AgentActivity';
import { ChatLoadingState } from './ChatLoadingState';
import { getAssistantMessageLayout } from './chatMessageLayout';
import { getPathName } from './presentation';
import { copyPlainText, copyRichText, createRichEmailHtml } from './richClipboard';

interface ChatMessageProps {
  message: LocalMessage;
  streamingContent?: ChatContentBlock[];
  elapsed?: number;
  lastActivityTime?: number | null;
  provider: 'claude' | 'codex';
  interactionDisabled: boolean;
  showCarrierDiscoveryProgress: boolean;
  /** Set when the chat belongs to another user: their files are named, not fetched. */
  readOnlyOwnerName?: string;
  onSubmitInteraction?: (
    interaction: ChatInteraction,
    submission: ChatInteractionSubmission
  ) => Promise<boolean>;
  onInvokeCommand?: (command: string) => boolean | Promise<boolean>;
}

function MessageImageStrip({ images, ownerName }: { images: string[]; ownerName?: string }) {
  if (images.length === 0) return null;
  // Another user's file lives in their folder. Name it; never fetch it.
  if (ownerName !== undefined) {
    return (
      <div className="mb-2 flex flex-wrap gap-2">
        {images.map((imagePath) => (
          <span
            key={imagePath}
            className="flex max-w-56 items-center gap-1.5 rounded-md border border-surface-200 bg-surface-100 px-2 py-1 text-xs text-surface-600 dark:border-surface-700 dark:bg-surface-800 dark:text-surface-300"
          >
            <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{getPathName(imagePath)}</span>
            <span className="shrink-0 text-surface-500 dark:text-surface-400">Owned by {ownerName}</span>
          </span>
        ))}
      </div>
    );
  }
  return (
    <div className="mb-2 flex flex-wrap gap-2">
      {images.map((imagePath) => {
        const name = getPathName(imagePath);
        const url = api.files.getDownloadUrl(imagePath, true);
        return (
          <a
            key={imagePath}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="group relative block h-20 w-28 overflow-hidden rounded-md border border-surface-200 bg-surface-100 dark:border-surface-700 dark:bg-surface-800"
            title={imagePath}
          >
            <img src={url} alt={name} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
            <span className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-0.5 text-[10px] text-white">{name}</span>
          </a>
        );
      })}
    </div>
  );
}

function MarkdownCodeBlock({ children }: { children?: ReactNode }) {
  const [copied, setCopied] = useState(false);
  const preRef = useRef<HTMLPreElement>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const childClassName = isValidElement<{ className?: string }>(children)
    ? children.props.className
    : undefined;
  const language = childClassName?.replace(/^language-/, '') || 'Code';

  useEffect(() => () => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  }, []);

  const handleCopy = async () => {
    try {
      await copyPlainText(preRef.current?.textContent || '');
      setCopied(true);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => setCopied(false), 1_500);
    } catch {
      toast.error('Could not copy code');
    }
  };

  return (
    <div className="my-2 overflow-hidden rounded-lg border border-surface-700 bg-surface-900" data-code-block>
      <div className="flex h-8 items-center justify-between border-b border-surface-700 px-3" data-copy-control>
        <span className="font-mono text-[11px] text-surface-400">{language}</span>
        <button
          type="button"
          onClick={() => void handleCopy()}
          className="flex h-6 items-center gap-1 rounded-md px-1.5 text-[11px] text-surface-400 transition-colors hover:bg-surface-800 hover:text-surface-100"
          aria-label="Copy code"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre ref={preRef} className="overflow-x-auto p-3">{children}</pre>
    </div>
  );
}

function MarkdownMessage({ content, contentRef }: { content: string; contentRef?: Ref<HTMLDivElement> }) {
  return (
    <div ref={contentRef}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
        a: ({ href, children, ...props }) => {
          const isExternal = href?.startsWith('http://') || href?.startsWith('https://');
          return (
            <a
              href={href}
              {...props}
              {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="text-primary-600 hover:underline dark:text-primary-400"
            >
              {children}
            </a>
          );
        },
        code: ({ className, children, ...props }) =>
          !className ? (
            <code {...props} className="rounded bg-surface-200 px-1 py-0.5 font-mono text-xs text-primary-700 dark:bg-surface-700 dark:text-primary-300">{children}</code>
          ) : (
            <code {...props} className={className}>{children}</code>
          ),
        pre: ({ children }) => <MarkdownCodeBlock>{children}</MarkdownCodeBlock>,
        p: ({ children, ...props }) => <p {...props} className="text-sm leading-relaxed">{children}</p>,
        ul: ({ children, ...props }) => <ul {...props} className="my-1 list-inside list-disc space-y-0.5 text-sm">{children}</ul>,
        ol: ({ children, ...props }) => <ol {...props} className="my-1 list-inside list-decimal space-y-0.5 text-sm">{children}</ol>,
        blockquote: ({ children, ...props }) => (
          <blockquote {...props} className="my-2 border-l-2 border-primary-500 pl-3 italic text-surface-600 dark:text-surface-400">{children}</blockquote>
        ),
        table: ({ children, ...props }) => <div className="my-2 overflow-x-auto"><table {...props} className="w-full border-collapse text-sm">{children}</table></div>,
        th: ({ children, ...props }) => <th {...props} className="border border-surface-300 bg-surface-200 px-2 py-1 text-left font-medium dark:border-surface-600 dark:bg-surface-700">{children}</th>,
        td: ({ children, ...props }) => <td {...props} className="border border-surface-300 px-2 py-1 dark:border-surface-600">{children}</td>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

export const ChatMessage = memo(function ChatMessage({
  message,
  streamingContent,
  elapsed,
  lastActivityTime,
  interactionDisabled,
  showCarrierDiscoveryProgress,
  readOnlyOwnerName,
  onSubmitInteraction,
  onInvokeCommand,
}: ChatMessageProps) {
  const { user } = useAuthStore();
  const timeZone = useSettingsStore((state) => state.contextTimezone);
  const { role, content, isPending, isStreaming, timestamp } = message;
  const images = message.images ?? [];
  const messageContentRef = useRef<HTMLDivElement>(null);
  const [responseCopied, setResponseCopied] = useState(false);
  const responseCopyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // A copied chat keeps the original writer of each user message.
  // null = persisted with no/deleted author (never reassign to the chat owner).
  // undefined = optimistic pending send; fall back to the current user only.
  const authorName = typeof message.authorUsername === 'string'
    ? message.authorUsername
    : message.authorUsername === null
      ? 'Deleted user'
      : user?.username ?? readOnlyOwnerName;
  const avatarLetter = authorName?.charAt(0).toUpperCase() || '?';
  const applicationSurface = useMemo(
    () => parseCarrierInitializationSurface(content),
    [content]
  );
  const trafficPerformanceDraftId = applicationSurface !== null
    && typeof applicationSurface === 'object'
    ? applicationSurface.draftId
    : undefined;
  const assistantLayout = getAssistantMessageLayout(
    message.interaction?.kind === 'service_definition_form' || Boolean(applicationSurface)
  );
  const formattedTime = useMemo(
    () => formatServerTimestamp(timestamp, timeZone, { hour: '2-digit', minute: '2-digit' }),
    [timestamp, timeZone]
  );

  useEffect(() => () => {
    if (responseCopyTimerRef.current) clearTimeout(responseCopyTimerRef.current);
  }, []);

  const handleCopyResponse = async () => {
    const contentElement = messageContentRef.current;
    if (!contentElement) return;

    try {
      await copyRichText(content, createRichEmailHtml(contentElement));
      setResponseCopied(true);
      if (responseCopyTimerRef.current) clearTimeout(responseCopyTimerRef.current);
      responseCopyTimerRef.current = setTimeout(() => setResponseCopied(false), 1_500);
    } catch {
      toast.error('Could not copy response');
    }
  };

  if (message.isInteractionResponse) return null;

  if (role === 'user') {
    return (
      <div className="flex items-start gap-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300" title={authorName}>{avatarLetter}</div>
        <div className="flex max-w-[80%] flex-col">
          <div className={cn('rounded-lg bg-primary-50 px-4 py-3 text-surface-900 dark:bg-primary-950/40 dark:text-surface-100', isPending && 'opacity-70')}>
            <MessageImageStrip images={images} ownerName={readOnlyOwnerName} />
            {content && <p className="whitespace-pre-wrap break-words text-sm">{content}</p>}
          </div>
          <div className="mt-1 flex items-center gap-2 px-1">
            {isPending ? (
              <span className="flex items-center gap-1 text-xs text-surface-500 dark:text-surface-400"><Loader2 className="h-3 w-3 animate-spin" />Sending...</span>
            ) : formattedTime ? (
              <span className="text-xs text-surface-500 dark:text-surface-400">{formattedTime}</span>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  if (role === 'assistant') {
    return (
      <div className="flex items-start justify-end gap-3">
        <div className={assistantLayout.container}>
          <div className={cn(
            assistantLayout.bubble,
            'rounded-lg border border-surface-200 bg-white px-4 py-3 text-surface-900 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-100'
          )}>
            {isStreaming && streamingContent && streamingContent.length > 0 && (
              <AgentActivity contentBlocks={streamingContent} elapsed={elapsed} lastActivityTime={lastActivityTime} />
            )}
            {isStreaming && !content && (!streamingContent || streamingContent.length === 0) && (
              <ChatLoadingState elapsed={elapsed} />
            )}
            {content && !applicationSurface && (
              <div className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1 dark:prose-invert" aria-live={isStreaming ? 'polite' : undefined}>
                <MarkdownMessage content={content} contentRef={messageContentRef} />
                {isStreaming && <span className="ml-0.5 inline-block h-4 w-0.5 translate-y-0.5 animate-[cursor-blink_900ms_ease-in-out_infinite] rounded-full bg-primary-500 align-middle" aria-hidden="true" />}
              </div>
            )}
            {!isStreaming && message.grounding === 'unsupported' && (
              <div
                role="note"
                className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                <span>
                  No data was read for this answer. Any figures above may come from earlier messages or may be wrong. Ask for a fresh read to confirm them.
                </span>
              </div>
            )}
            {message.interaction?.kind === 'approval' && onSubmitInteraction && (
              <ApprovalInteraction interaction={message.interaction} disabled={interactionDisabled} onSubmit={onSubmitInteraction} />
            )}
            {message.interaction?.kind === 'service_definition_form' && onSubmitInteraction && (
              <ServiceDefinitionForm
                interaction={message.interaction}
                disabled={interactionDisabled}
                showDiscoveryProgress={showCarrierDiscoveryProgress}
                onSubmit={onSubmitInteraction}
              />
            )}
            {applicationSurface === 'overview' && (
              <CarrierInitializationOverview
                disabled={interactionDisabled}
                onInvokeCommand={onInvokeCommand}
              />
            )}
            {applicationSurface === 'organizer' && (
              <CarrierMappingDrafts
                disabled={interactionDisabled}
                defaultOpen
                defaultOpenKey={String(message.id)}
              />
            )}
            {applicationSurface === 'audit_review' && (
              <CarrierAuditReview
                disabled={interactionDisabled}
                surfaceKey={String(message.id)}
              />
            )}
            {applicationSurface === 'rate_summary' && (
              <DomesticServiceRateSummary direction="outbound" disabled={interactionDisabled} />
            )}
            {applicationSurface === 'customer_rate_summary' && (
              <DomesticServiceRateSummary direction="inbound" disabled={interactionDisabled} />
            )}
            {(applicationSurface === 'traffic_performance'
              || (applicationSurface !== null
                && typeof applicationSurface === 'object'
                && applicationSurface.kind === 'traffic_performance')) && (
              <DomesticTrafficPerformance
                disabled={interactionDisabled}
                draftId={trafficPerformanceDraftId}
              />
            )}
          </div>
          {content && !applicationSurface && !isStreaming && (
            <button
              type="button"
              onClick={() => void handleCopyResponse()}
              className="mt-1 flex h-7 items-center gap-1.5 rounded-md px-2 text-[11px] text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-800 dark:text-surface-500 dark:hover:bg-surface-800 dark:hover:text-surface-200"
              title="Copy response as rich email text"
              aria-label="Copy response as rich email text"
            >
              {responseCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {responseCopied ? 'Copied for email' : 'Copy for email'}
            </button>
          )}
          {formattedTime && !isStreaming && <div className="mt-1 flex items-center gap-2 px-1"><span className="text-xs text-surface-500 dark:text-surface-400">{formattedTime}</span></div>}
        </div>
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-700 shadow-sm">
          <BrandLogo className="h-5 w-5 [--mark-ink:#fff]" />
        </div>
      </div>
    );
  }

  if (role === 'system') {
    return (
      <div className="flex items-center justify-center">
        <div className="flex items-center gap-2 rounded-full border border-surface-200 bg-surface-100 px-4 py-2 dark:border-surface-700 dark:bg-surface-800">
          <Info className="h-4 w-4 text-surface-500 dark:text-surface-400" />
          <span className="text-sm text-surface-600 dark:text-surface-400">{content}</span>
        </div>
      </div>
    );
  }

  return null;
});
